import { useEffect, useState } from 'react';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import { TextInput } from '@components/shared/form/TextInput';
import { AddressInfo, CDX_ACTIONS, UpdateAddressInfo } from 'types/form';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { countryStates } from '@shared/constants/countryStates';
import { addressTypeOptions } from '@shared/constants/addressTypes';
import useForm, { validate } from '@shared/hooks/useForm';
import { monthsBetween, todaysDate } from 'utils/dateUtils';
import { PriceInput } from '@components/shared/form/PriceTextInput';
import { getContactData } from '@services/utils';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { createAddressHistory } from '@services/addressHistory';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';
import { formAddressFormatResponse } from './CurrentAddressInfoForm';


export type ICurrentAddressManuallyInfo = Omit<UpdateAddressInfo, 'county' | 'isCurrent' | 'monthsAtAddress'> & {
  moveInDate: string;
};
export const addAddress = (address: ICurrentAddressManuallyInfo) => {
  return {
    streetLine1: address?.streetLine1 ?? '',
    streetLine2: address?.streetLine2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zip ?? '',
    moveInDate: 'moveInDate' in address ? address.moveInDate : '',
    moveOutDate: new Date().toISOString().split('T')[0],
    type: address?.type ?? '',
    monthlyPayment: address?.monthlyPayment,
  };
};
const CurrentAddressInfoManuallyForm = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();

  const { trackFormSubmit, trackPage, startInteraction } = useAnalytics({
    ...SEGMENT_SCREEN_PARAMS.ADDRESS_LOOP,
    field_count: 8,
    required_field_count: 6,
  });

  const [contactAdddressResponse, setcontactAdddressResponse] = useState<AddressInfo[] | []>([]);

  const { currentStep, setAddressHistory, dispatch, isLoading, submitStart, submitSuccess } = useFormContext();
  const contactData = getContactData();

  const { errors, handleChange, handleSubmit, values } = useForm<ICurrentAddressManuallyInfo>({
    initialValues: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      zip: '',
      moveInDate: '',
      type: '',
      monthlyPayment: 0,
    },
    validationSchema: {
      streetLine1: validate.string('streetLine1').required(t('addressHistory.formValidation.address')),
      streetLine2: validate.string('streetLine2'),
      city: validate.string('city').required(t('addressHistory.formValidation.city')),
      state: validate.string('state').required(t('addressHistory.formValidation.state')),
      zip: validate
        .string('zip')
        .required(t('addressHistory.formValidation.zipcode'))
        .matches(/^\d{5}$/, t('addressHistory.formValidation.validZipcode')),
      moveInDate: validate.string('moveInDate').required(t('addressHistory.formValidation.moveInDate')),
      type: validate.string('type').required(t('addressHistory.formValidation.residentType')),
      monthlyPayment: validate.number('monthlyPayment'),
    },

    onSubmit: async (values) => {
      const { moveInDate } = values;
      const months = monthsBetween(moveInDate, new Date());
      const formatFormAddress = addAddress(values);
      const formatAddressResponse = [formatFormAddress, ...formAddressFormatResponse(contactAdddressResponse)];
      const trackingFormattedAddressResponse = formatAddressResponse.map(({ zip, state, city }) => ({
        zip,
        state,
        city,
      }));
      if (months < 24) {
        trackFormSubmit('Address Information Provided', {
          completed_status: false,
          submission_method: 'manual',
          addresses_provided_count: 1,
          total_months_provided: months,
          months_at_address: months,
          months_of_history_required: 24,
          months_of_history_remaining: Math.abs(24 - months),
          form_context: 'address_loop',
          ...trackingFormattedAddressResponse,
        });
        setAddressHistory(formatAddressResponse);
        next(currentStep, 'postSubmitOutcome', { monthsAtAddress: months}, false);
      } else {
        submitStart();

        const createAddressHistoryResponse = await createAddressHistory(formatAddressResponse, contactData?.contactId);

        if ('error' in createAddressHistoryResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: createAddressHistoryResponse,
          });
        } else {
          trackFormSubmit('Address Information Provided', {
            completed_status: true,
            submission_method: 'manual',
            addresses_provided_count: 1,
            total_months_provided: months,
            months_at_address: months,
            months_of_history_required: 24,
            months_of_history_remaining: Math.abs(24 - months),
            form_context: 'address_loop',
            ...trackingFormattedAddressResponse,
          });
          trackFormSubmit('Form Submitted', {
            submission_status: true,
            submission_method: 'manual',
            addresses_provided_count: 1,
            months_of_history_captured: months,
            months_of_history_required: 24,
            months_of_history_remaining: Math.abs(24 - months),
            forward_screen_name: 'employment_loop',
          });
          submitSuccess();
          next(currentStep, 'postSubmitOutcome', { monthsAtAddress: months}, true);
        }
      }
      
    },
    onValidationError: (errors, values) => {
      const { moveInDate } = values;
      const months = monthsBetween(moveInDate, new Date());
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'manual',
        addresses_provided_count: 1,
        months_of_history_captured: months,
        months_of_history_required: 24,
        months_of_history_remaining: Math.abs(24 - months),
        forward_screen_name: 'address_loop',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  const handleInputChange = <K extends keyof ICurrentAddressManuallyInfo>(field: K, value: ICurrentAddressManuallyInfo[K]) => {
    handleChange(field, value);
  };

  useEffect(() => {
    if (contactData) {
      if (contactData?.addresses?.length > 0) {
        setcontactAdddressResponse(contactData?.addresses);
      }
    }
  }, []);

  useEffectOnce(() => trackPage(t('pageTitles.AddressHistory')));

  return (
    <div className="iLendingCDX-full-width">
      <h1 className="iLendingCDX-form-title">{t('currentAddressManually.title')}</h1>
      <h1 className="iLendingCDX-form-title--small iLendingCDX-mb-4">{t('currentAddressManually.title2')}</h1>

      <form
        onSubmit={handleSubmit}
        onFocus={startInteraction}
      >
        <TextInput
          label={t('currentAddressManually.form.address')}
          className={'iLendingCDX-mb-2'}
          name="address"
          errorMessage={errors.streetLine1}
          onChange={(e) => handleInputChange('streetLine1', e.target.value)}
        />
        <TextInput
          label={t('currentAddressManually.form.APTSuiteNumber')}
          className={'iLendingCDX-mb-2'}
          name="APTSuiteNumber"
          value={values?.streetLine2}
          errorMessage={errors.streetLine2}
          onChange={(e) => handleInputChange('streetLine2', e.target.value)}
        />
        <TextInput
          label={t('currentAddressManually.form.city')}
          className={'iLendingCDX-mb-2'}
          name="city"
          value={values?.city}
          errorMessage={errors.city}
          onChange={(e) => handleInputChange('city', e.target.value)}
        />

        <CustomSelect
          label={t('currentAddressManually.form.state')}
          className={'iLendingCDX-mb-4'}
          name="state"
          options={countryStates}
          value={values?.state}
          errorMessage={errors.state}
          onChange={(e) => handleInputChange('state', e.target.value)}
        />
        <TextInput
          label={t('currentAddressManually.form.zipcode')}
          className={'iLendingCDX-mb-2'}
          name="zipcode"
          value={values?.zip}
          errorMessage={errors.zip}
          onChange={(e) => handleInputChange('zip', e.target.value)}
        />

        <TextInput
          label={t('currentAddressManually.form.moveInDate')}
          className={'iLendingCDX-mb-2'}
          name="moveInDate"
          type="date"
          value={values?.moveInDate}
          errorMessage={errors.moveInDate}
          max={todaysDate}
          onChange={(e) => handleInputChange('moveInDate', e.target.value as ICurrentAddressManuallyInfo['moveInDate'])}
        />
        <CustomSelect
          label={t('currentAddressManually.form.residentType')}
          className={'iLendingCDX-mb-4'}
          name="residentType"
          options={addressTypeOptions}
          value={values?.type}
          errorMessage={errors.type}
          onChange={(e) => handleInputChange('type', e.target.value)}
        />

        <PriceInput
          label={t('currentAddressManually.form.monthlyPaymentAmount')}
          className={'iLendingCDX-mb-8'}
          name="monthlyPaymentAmount"
          value={values?.monthlyPayment}
          errorMessage={errors.monthlyPayment}
          onChange={(value) => handleInputChange('monthlyPayment', value as ICurrentAddressManuallyInfo['monthlyPayment'])}
        />
        <Button
          type="submit"
          className="iLendingCDX-button--rounded "
          size="lg"
          isLoading={isLoading}
        >
          {t('currentAddressManually.form.continue')}
        </Button>
      </form>
    </div>
  );
};
export default CurrentAddressInfoManuallyForm;
